<template>
  <master-layout no-bottom-pad>
    <!-- pb inside the bg-grey-5 root so the nav-pill clearance keeps the page background -->
    <div class="min-h-screen bg-grey-5 w-full pb-28 lg:pb-0">
      <!-- Header -->
      <!-- Left-aligned on desktop like the Resources page (md:px-[56px], no centered column) -->
      <div
        class="flex flex-col gap-6 px-6 pt-[24px] pb-4 lg:pt-[56px] md:px-[56px]"
        :class="{ '!pt-[68px]': isIos, '!pt-[max(24px,env(safe-area-inset-top))]': isAndroid }"
      >
        <h1 class="page-title">More</h1>
      </div>

      <!-- Loading skeleton -->
      <div v-if="loading" class="flex flex-col gap-8 px-6 pb-10 md:px-[56px]">
        <!-- Card-section skeleton: heading + 3:2 card with peeking neighbour + dots -->
        <div class="flex flex-col gap-3">
          <div class="h-5 w-24 bg-grey-10 rounded animate-pulse" />
          <div class="flex gap-4 overflow-hidden">
            <div class="w-[300px] shrink-0 aspect-[3/2] bg-grey-10 rounded-2xl animate-pulse" />
            <div class="flex-1 aspect-[3/2] bg-grey-10 rounded-2xl animate-pulse" />
          </div>
          <div class="flex justify-center gap-2">
            <div class="h-2 w-4 bg-grey-10 rounded-full animate-pulse" />
            <div v-for="d in 3" :key="d" class="h-2 w-2 bg-grey-10 rounded-full animate-pulse" />
          </div>
        </div>
        <div
          v-for="n in 2"
          :key="n"
          class="rounded-3xl border border-grey-20 overflow-hidden md:max-w-[560px]"
        >
          <div class="bg-white px-6 py-3 border-b border-grey-20">
            <div class="h-4 w-24 bg-grey-10 rounded animate-pulse" />
          </div>
          <div
            v-for="m in 2"
            :key="m"
            class="bg-white px-6 py-3 flex items-center justify-between border-b border-grey-20"
          >
            <div class="h-5 w-5 bg-grey-10 rounded-full animate-pulse shrink-0" />
            <div class="h-4 w-[218px] bg-grey-10 rounded animate-pulse shrink-0" />
            <div class="h-2.5 w-1.5 bg-grey-10 rounded animate-pulse shrink-0" />
          </div>
        </div>
      </div>

      <!-- Sections: cards present → carousel section; otherwise the classic list box -->
      <!-- 32px between categories per design -->
      <div v-else class="flex flex-col gap-8 px-6 pb-10 md:px-[56px]">
        <template v-for="section in moreSections" :key="section.id">
          <MoreCardSection
            v-if="section.cards?.length"
            :id="sectionAnchor(section)"
            :ref="setCardSectionRef"
            :section="section"
            @card-click="handleCardClick"
          />
          <div
            v-else-if="section.items?.length || section.title"
            :id="sectionAnchor(section)"
            class="rounded-3xl border border-grey-20 overflow-hidden md:max-w-[560px] anchor-scroll-margin"
          >
            <!-- Section title header -->
            <div v-if="section.title" class="bg-white px-6 py-3 border-b border-grey-20">
              <p
                class="font-aktiv-grotesk-bold font-bold text-base leading-6 text-[#1b2a6b]"
                style="
                  font-feature-settings:
                    'liga' off,
                    'clig' off;
                "
              >
                {{ section.title }}
              </p>
            </div>

            <!-- Items -->
            <!-- Icon sits 16px (gap-4) from the label per Figma; chevron pinned right -->
            <button
              v-for="item in section.items"
              :id="item.slug || undefined"
              :key="item.id"
              class="bg-white w-full flex items-center justify-between gap-4 px-6 py-3 border-b border-grey-20 active:bg-grey-5 transition-colors duration-100 anchor-scroll-margin"
              :data-event-tracking="`gensapp-more-${item.label?.toLowerCase().replace(/\s+/g, '-')}`"
              @click="handleItemClick(item)"
            >
              <img
                v-if="item.icon"
                :src="item.icon?.url"
                alt=""
                class="shrink-0 w-6 h-6 object-contain"
              />
              <span v-else class="shrink-0 w-6 h-6" />
              <span class="font-aktiv-grotesk text-base leading-6 text-[#1b2a6b] flex-1 text-left">
                {{ item.label }}
              </span>
              <img src="/assets/icons/chevron-right-grey.svg" class="shrink-0" />
            </button>
          </div>
        </template>
      </div>
    </div>
  </master-layout>
</template>

<script>
import { Browser } from '@capacitor/browser';
import { mapState } from 'vuex';
import platformMixin from '@/mixins/platformMixin';
import MoreCardSection from '@/components/MorePage/MoreCardSection.vue';
import { appendPrefillParams } from '@/utils/prefill';

export default {
  name: 'MorePage',
  components: { MoreCardSection },
  mixins: [platformMixin],
  data() {
    return { loading: true, cardSectionRefs: [], openedHash: null };
  },
  computed: {
    ...mapState('cms', ['moreSections']),
    ...mapState('profile', [
      'id',
      'name',
      'organization',
      'email',
      'country',
      'groupName',
      'hashId',
    ]),
    // Same profile shape as HtmlToVue passes for gpc-prefillable links
    prefillProfile() {
      return {
        id: this.id,
        name: this.name,
        email: this.email,
        organization: this.organization,
        country: this.country,
        groupName: this.groupName,
        hashId: this.hashId,
      };
    },
  },
  beforeUpdate() {
    this.cardSectionRefs = [];
  },
  // Fetch here rather than created(): Ionic serves this page from cache on
  // tab re-entry (no remount), and the page must always show the latest CMS
  // data. Stale-while-revalidate — the skeleton only shows on first load;
  // re-entries refresh silently over the existing sections.
  async ionViewWillEnter() {
    this.loading = !this.moreSections?.length;
    await this.$store.dispatch('cms/fetchMoreSections');
    this.loading = false;
    this.$nextTick(() => {
      // Glider must re-measure on re-enter or slides stack / keep stale widths
      this.cardSectionRefs.forEach(section => section?.refresh?.());
      this.openHashTarget();
    });
  },
  // Clear the double-fire guard on leave so a fresh deep link with the same
  // anchor opens again. Re-entry protection comes from the hash strip in
  // openHashTarget, not from this guard.
  ionViewDidLeave() {
    this.openedHash = null;
  },
  watch: {
    // Support hash changes while the page is already showing (e.g. a card
    // linking to /more#websites)
    '$route.hash'() {
      if (this.$route.path.endsWith('/more')) this.openHashTarget();
    },
  },
  methods: {
    setCardSectionRef(el) {
      if (el) this.cardSectionRefs.push(el);
    },
    // Anchor id: the CMS slug when set, else derived from the title,
    // e.g. "Venue Info" → /more#venue-info
    sectionAnchor(section) {
      if (section.slug) return section.slug;
      const slug = (section.title || '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      return slug || `section-${section.id}`;
    },
    openHashTarget() {
      const hash = this.$route.hash?.slice(1);
      // Guard a double-fire within the same entry (ionViewWillEnter and the
      // hash watcher can both run). The real re-entry protection is the hash
      // strip below.
      if (!hash || hash === this.openedHash) return;
      this.openedHash = hash;
      // Small delay so the freshly rendered sections have laid out
      setTimeout(() => {
        // Silently position the card into view (no animation) so closing the
        // browser lands the user back on the thumbnail — without any visible
        // scroll dance while the target opens.
        const el = document.getElementById(hash);
        if (el) {
          const rail = el.closest('.glider');
          if (rail) {
            // Card inside a carousel: WebKit doesn't honor scroll-margin
            // through nested scrollers, so position the section vertically
            // (its margin works) and set the rail's horizontal offset to the
            // card ourselves. Instant, not smooth — it happens behind the
            // browser so there's nothing to animate.
            const sectionEl = rail.closest('.anchor-scroll-margin') || rail;
            sectionEl.scrollIntoView({ block: 'start' });
            const left =
              el.getBoundingClientRect().left - rail.getBoundingClientRect().left + rail.scrollLeft;
            rail.scrollLeft = Math.max(0, left);
          } else {
            el.scrollIntoView({ block: 'start' });
          }
        }
        const target = this.findAnchorTarget(hash);
        if (!target) return;
        // Consume the hash: strip it from the route (replace = no new history
        // entry) BEFORE opening, so pressing back returns to a clean /more that
        // can't re-fire this open. Without it the #hash lingers and every back /
        // forward / tab re-entry re-triggers the link — an inescapable loop.
        // Awaited via the promise chain: pushing /url synchronously would abort
        // the replace, leaving the hash behind.
        this.$router
          .replace({ path: this.$route.path, query: this.$route.query })
          .catch(() => {})
          .finally(() => this.openAnchorTarget(target));
      }, 150);
    },
    // Match a hash to a card or list item by its CMS slug.
    findAnchorTarget(hash) {
      for (const section of this.moreSections || []) {
        const card = (section.cards || []).find(c => c.slug === hash);
        if (card) return { type: 'card', data: card };
        const item = (section.items || []).find(i => i.slug === hash);
        if (item) return { type: 'item', data: item };
      }
      return null;
    },
    openAnchorTarget(target) {
      if (target.type === 'card') this.handleCardClick(target.data);
      else this.handleItemClick(target.data);
    },
    handleCardClick(card) {
      if (!card?.url) return;
      let url = card.prefillable ? appendPrefillParams(card.url, this.prefillProfile) : card.url;
      // External links follow the CMS-link standard: route through /url so
      // they open in the in-app browser and get tracked (analytics backend).
      // isExternal covers CMS urls authored without a protocol (gens.app/give).
      if (card.isExternal || /^https?:\/\//i.test(url)) {
        if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
        url = this.withPrefilledEmail(url);
        this.$router.push(
          `/url?q=${encodeURIComponent(url)}&from=${encodeURIComponent(this.$route.path)}`,
        );
      } else {
        this.$router.push(url);
      }
    },
    async handleItemClick(item) {
      if (!item?.url) return;
      if (item.isExternal || item.url?.startsWith('http')) {
        try {
          await Browser.open({ url: this.withPrefilledEmail(item.url) });
        } catch {
          // noop
        }
      } else {
        this.$router.push(item.url);
      }
    },
    // The give page reads prefilled_email to pre-populate
    withPrefilledEmail(url) {
      if (!url?.includes('gens.app/give') || !this.email) return url;
      try {
        const giveUrl = new URL(url);

        if (!giveUrl.searchParams.get('prefilled_email')) {
          giveUrl.searchParams.set('prefilled_email', this.email);
        }
        return giveUrl.toString();
      } catch {
        return url;
      }
    },
  },
};
</script>
